import { useState, useEffect } from 'react';
import { AdjustmentSettings } from '@/types/adjustment-filters';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface UseAdjustmentSettingsProps {
  estimateId: string;
}

export const useAdjustmentSettings = ({ estimateId }: UseAdjustmentSettingsProps) => {
  const [settings, setSettings] = useState<AdjustmentSettings>({
    filters: [],
    discountPercentage: undefined,
    discountType: 'percentage'
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Load settings from database
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const { data, error } = await supabase
          .from('estimates')
          .select('adjustment_metadata')
          .eq('id', estimateId)
          .single();

        if (error) throw error;

        if (data?.adjustment_metadata && typeof data.adjustment_metadata === 'object') {
          const loadedSettings = data.adjustment_metadata as any;
          setSettings({
            filters: Array.isArray(loadedSettings.filters) ? loadedSettings.filters : [],
            discountPercentage: typeof loadedSettings.discountPercentage === 'number' ? loadedSettings.discountPercentage : undefined,
            discountType: loadedSettings.discountType === 'percentage' ? 'percentage' : 'percentage'
          });
        }
      } catch (error) {
        console.error('Error loading adjustment settings:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (estimateId) {
      loadSettings();
    }
  }, [estimateId]);

  // Save settings to database
  const saveSettings = async (newSettings: AdjustmentSettings) => {
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('estimates')
        .update({ adjustment_metadata: newSettings as any })
        .eq('id', estimateId);

      if (error) throw error;

      setSettings(newSettings);
      toast.success('Adjustment settings saved');
    } catch (error) {
      console.error('Error saving adjustment settings:', error);
      toast.error('Failed to save adjustment settings');
    } finally {
      setIsSaving(false);
    }
  };

  const updateSettings = (newSettings: AdjustmentSettings) => {
    setSettings(newSettings);
  };

  const hasAdjustments = (settings?.filters?.length || 0) > 0 || (typeof settings?.discountPercentage === 'number' && settings.discountPercentage > 0);

  return {
    settings,
    isLoading,
    isSaving,
    hasAdjustments,
    updateSettings,
    saveSettings
  };
};
